import React, { useEffect, useMemo, useState } from 'react';
import { Package, RefreshCw, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from './Auth';
import { useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import InventoryCatalogPanel from './Inventory/InventoryCatalogPanel';
import InventoryStockPanel from './Inventory/InventoryStockPanel';
import InventoryModals from './Inventory/InventoryModals';

const apiBaseUrl = process.env.NODE_ENV === 'development'
  ? `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}`
  : `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:8000';

const statusOptions = [
  { value: 'ordered', label: 'Commande' },
  { value: 'in_stock', label: 'En stock' },
  { value: 'at_client', label: 'Chez le proprietaire' },
  { value: 'removed', label: 'Supprime' },
];

const statusClassMap = {
  ordered: 'bg-amber-100 text-amber-700',
  in_stock: 'bg-emerald-100 text-emerald-700',
  at_client: 'bg-sky-100 text-sky-700',
  removed: 'bg-rose-100 text-rose-700',
};

const statusBorderMap = {
  ordered: 'border-amber-200/80 border-l-4 border-l-amber-400',
  in_stock: 'border-emerald-200/80 border-l-4 border-l-emerald-400',
  at_client: 'border-sky-200/80 border-l-4 border-l-sky-400',
  removed: 'border-rose-200/80 border-l-4 border-l-rose-400',
};

const getStatusLabel = (status) => {
  const match = statusOptions.find((option) => option.value === status);
  return match ? match.label : status;
};

const toOptionalNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const normalizeDateTime = (value) => {
  if (!value) return null;
  return value;
};

const normalizeComponentSnapshot = (components = []) =>
  components
    .map((component) => ({
      child_item_id: Number(component.child_item_id) || null,
      child_version_id: component.child_version_id ? Number(component.child_version_id) : null,
      quantity: Number(component.quantity) || 1,
      notes: component.notes ? component.notes.trim() : '',
    }))
    .sort((a, b) => {
      if (a.child_item_id !== b.child_item_id) {
        return (a.child_item_id || 0) - (b.child_item_id || 0);
      }
      if ((a.child_version_id || 0) !== (b.child_version_id || 0)) {
        return (a.child_version_id || 0) - (b.child_version_id || 0);
      }
      if (a.quantity !== b.quantity) {
        return a.quantity - b.quantity;
      }
      return (a.notes || '').localeCompare(b.notes || '');
    });

const createDraftComponentId = () =>
  `component-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createDraftComponent = (type = 'existing') => ({
  id: createDraftComponentId(),
  type,
  quantity: 1,
  notes: '',
  child_item_id: '',
  child_version_id: '',
  new_item: {
    name: '',
    description: '',
    purchase_url: '',
    version: {
      label: '',
      description: '',
    },
    components: [],
  },
});

const addComponentToTree = (components, parentId, component) => {
  if (!parentId) return [...components, component];
  return components.map((entry) => {
    if (entry.id === parentId && entry.type === 'new') {
      const nextChildren = [...(entry.new_item?.components || []), component];
      return {
        ...entry,
        new_item: {
          ...entry.new_item,
          components: nextChildren,
        },
      };
    }
    if (entry.type === 'new' && entry.new_item?.components?.length) {
      return {
        ...entry,
        new_item: {
          ...entry.new_item,
          components: addComponentToTree(entry.new_item.components, parentId, component),
        },
      };
    }
    return entry;
  });
};

const updateComponentInTree = (components, componentId, updater) =>
  components.map((entry) => {
    if (entry.id === componentId) {
      return updater(entry);
    }
    if (entry.type === 'new' && entry.new_item?.components?.length) {
      return {
        ...entry,
        new_item: {
          ...entry.new_item,
          components: updateComponentInTree(entry.new_item.components, componentId, updater),
        },
      };
    }
    return entry;
  });

const removeComponentFromTree = (components, componentId) =>
  components
    .filter((entry) => entry.id !== componentId)
    .map((entry) => {
      if (entry.type === 'new' && entry.new_item?.components?.length) {
        return {
          ...entry,
          new_item: {
            ...entry.new_item,
            components: removeComponentFromTree(entry.new_item.components, componentId),
          },
        };
      }
      return entry;
    });

const countComponentsInTree = (components = []) =>
  components.reduce((total, entry) => {
    const nested =
      entry.type === 'new' && entry.new_item?.components?.length
        ? countComponentsInTree(entry.new_item.components)
        : 0;
    return total + 1 + nested;
  }, 0);

const InventoryManagement = () => {
  const { getAuthHeaders, user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('stock');
  const [items, setItems] = useState([]);
  const [catalogItemId, setCatalogItemId] = useState(null);
  const [itemDetail, setItemDetail] = useState(null);
  const [itemSearch, setItemSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [stockStatusFilter, setStockStatusFilter] = useState('');
  const [stockFilterItemId, setStockFilterItemId] = useState('');
  const [stockClientFilter, setStockClientFilter] = useState(() => {
    try {
      return localStorage.getItem('inventory_stock_client_filter') || '';
    } catch (err) {
      return '';
    }
  });
  const [stockFormItemId, setStockFormItemId] = useState('');
  const [stockFormVersions, setStockFormVersions] = useState([]);
  const [loading, setLoading] = useState({
    items: false,
    detail: false,
    stock: false,
    stockVersions: false,
    quick: false,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    purchase_url: '',
  });
  const [editingItem, setEditingItem] = useState(null);
  const [newItemVersion, setNewItemVersion] = useState({
    label: '',
    description: '',
  });
  const [newItemComponents, setNewItemComponents] = useState([]);
  const [newItemComponentVersionMap, setNewItemComponentVersionMap] = useState({});
  const [newItemComponentVersionLoading, setNewItemComponentVersionLoading] = useState({});

  const [newVersion, setNewVersion] = useState({
    label: '',
    description: '',
  });

  const [componentForm, setComponentForm] = useState({
    child_item_id: '',
    child_version_id: '',
    quantity: 1,
    notes: '',
  });
  const [componentVersions, setComponentVersions] = useState([]);
  const [componentLoading, setComponentLoading] = useState(false);
  const [draftComponents, setDraftComponents] = useState([]);
  const [componentVersionMap, setComponentVersionMap] = useState({});
  const [componentVersionLoading, setComponentVersionLoading] = useState({});
  const [catalogVersionComponents, setCatalogVersionComponents] = useState({});
  const [catalogVersionLoading, setCatalogVersionLoading] = useState({});
  const [catalogExpandedComponents, setCatalogExpandedComponents] = useState({});
  const [catalogResolvedComponentVersions, setCatalogResolvedComponentVersions] = useState({});
  const [catalogLatestVersionMap, setCatalogLatestVersionMap] = useState({});
  const [catalogComponentWarnings, setCatalogComponentWarnings] = useState({});

  const [newStock, setNewStock] = useState({
    item_version_id: '',
    occurrence_name: '',
    status: 'ordered',
    client_id: '',
    project_id: '',
    location: '',
    tracking_number: '',
    ordered_at: '',
    received_at: '',
    shipped_at: '',
    removed_at: '',
    notes: '',
    quantity: 1,
    create_children: true,
  });

  const [quickStock, setQuickStock] = useState({
    item_name: '',
    description: '',
    version_label: '',
    occurrence_name: '',
    status: 'ordered',
    client_id: '',
    project_id: '',
    location: '',
    tracking_number: '',
    notes: '',
    quantity: 1,
  });
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [showQuickStockModal, setShowQuickStockModal] = useState(false);
  const [showExistingStockModal, setShowExistingStockModal] = useState(false);

  const [expandedStockKey, setExpandedStockKey] = useState(null);
  const [expandedStockNodes, setExpandedStockNodes] = useState({});
  const [selectedStockId, setSelectedStockId] = useState(null);
  const [stockEdits, setStockEdits] = useState({});
  const [openLogFor, setOpenLogFor] = useState(null);
  const [stockLogs, setStockLogs] = useState({});
  const [newLogMessage, setNewLogMessage] = useState({});
  const [stockChildren, setStockChildren] = useState({});
  const [loadingChildren, setLoadingChildren] = useState({});
  const [linkableStockItems, setLinkableStockItems] = useState([]);
  const [linkableSearch, setLinkableSearch] = useState('');
  const [compatibleLinkableItemIds, setCompatibleLinkableItemIds] = useState([]);
  const [selectedChildStockIds, setSelectedChildStockIds] = useState([]);
  const [loadingLinkable, setLoadingLinkable] = useState(false);
  const [showStockEditModal, setShowStockEditModal] = useState(false);
  const [editingStockItem, setEditingStockItem] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrStockItem, setQrStockItem] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [pendingExpandStockKey, setPendingExpandStockKey] = useState(null);

  const flashError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 4000);
  };

  const flashSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const restoreScroll = (scrollY) => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
    });
  };

  const collectLoadedStockIds = (rootId) => {
    if (!rootId) return [];
    const ids = new Set();
    const stack = [rootId];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current || ids.has(current)) continue;
      ids.add(current);
      const children = stockChildren[current] || [];
      children.forEach((child) => {
        if (child?.id && !ids.has(child.id)) {
          stack.push(child.id);
        }
      });
    }
    return Array.from(ids);
  };

  const refreshStockLogs = async (rootId, extraIds = []) => {
    const ids = new Set([
      ...collectLoadedStockIds(rootId),
      ...extraIds.filter(Boolean),
    ]);
    if (ids.size === 0) return;
    await Promise.all(Array.from(ids).map((id) => handleLoadLogs(id)));
  };

  const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

  const getStockShareUrl = (stockItem) =>
    `${window.location.origin}/inventory?stock=${stockItem.id}`;

  const openQrModal = (stockItem) => {
    setQrStockItem(stockItem);
    setShowQrModal(true);
  };

  const closeQrModal = () => {
    setShowQrModal(false);
    setQrStockItem(null);
    setQrDataUrl('');
  };

  const buildStockGroupKey = (stockItem) =>
    [
      stockItem.item_version_id || '',
      stockItem.occurrence_name || '',
      stockItem.status || '',
      stockItem.client_id || '',
      stockItem.project_id || '',
      stockItem.location || '',
      stockItem.tracking_number || '',
    ].join('|');

  const buildDraftComponentsFromVersion = (version) => {
    if (!version || !Array.isArray(version.components)) return [];
    return version.components.map((component) => ({
      id: component.id ? `component-${component.id}` : `component-${Date.now()}`,
      child_item_id: component.child_item_id ? String(component.child_item_id) : '',
      child_version_id: component.child_version_id ? String(component.child_version_id) : '',
      quantity: component.quantity || 1,
      notes: component.notes || '',
      child_item_name: component.child_item_name || '',
      child_version_number: component.child_version_number || null,
      child_version_label: component.child_version_label || '',
    }));
  };

  const filteredItems = useMemo(() => {
    if (!itemSearch.trim()) return items;
    const needle = itemSearch.toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(needle));
  }, [items, itemSearch]);

  const newItemComponentCount = useMemo(
    () => countComponentsInTree(newItemComponents),
    [newItemComponents],
  );

  const userIds = useMemo(() => {
    const ids = new Set();
    if (user?.id !== undefined && user?.id !== null) {
      ids.add(String(user.id));
    }
    if (user?.employee_id !== undefined && user?.employee_id !== null) {
      ids.add(String(user.employee_id));
    }
    return ids;
  }, [user]);

  const projectPickerOptions = useMemo(() => {
    if (!Array.isArray(projects) || projects.length === 0) return [];
    const statusPriority = {
      proposition: 0,
      completed: 1,
    };
    const decorated = projects.map((project) => {
      const status = String(project?.status || '').toLowerCase();
      const inProgress = status === 'in_progress';
      const assigned =
        inProgress &&
        userIds.size > 0 &&
        project?.owner_id !== undefined &&
        project?.owner_id !== null &&
        userIds.has(String(project.owner_id));
      return {
        project,
        rank: assigned ? 0 : inProgress ? 1 : 2,
        statusRank: statusPriority[status] ?? 99,
      };
    });
    decorated.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (a.rank === 2 && a.statusRank !== b.statusRank) {
        return a.statusRank - b.statusRank;
      }
      return (a.project.name || '').localeCompare(b.project.name || '');
    });
    return decorated.map((entry) => entry.project);
  }, [projects, userIds]);

  const filteredLinkableStockItems = useMemo(() => {
    if (!newStock.item_version_id || !newStock.create_children) return [];
    if (compatibleLinkableItemIds.length === 0) return [];
    const needle = linkableSearch.toLowerCase();
    const compatibleSet = new Set(compatibleLinkableItemIds);
    return linkableStockItems.filter((stockItem) => {
      if (compatibleSet.size > 0 && !compatibleSet.has(stockItem.item_id)) {
        return false;
      }
      const serial = (stockItem.serial_number || '').toLowerCase();
      const name = (stockItem.item_name || '').toLowerCase();
      return (
        !needle ||
        serial.includes(needle) ||
        name.includes(needle)
      );
    });
  }, [linkableStockItems, linkableSearch, compatibleLinkableItemIds, newStock.item_version_id]);

  const groupedStockItems = useMemo(() => {
    const groups = new Map();
    stockItems.forEach((stockItem) => {
      const key = buildStockGroupKey(stockItem);
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          items: [],
          primary: null,
          latestCreatedAt: 0,
          childTotal: 0,
        });
      }
      const group = groups.get(key);
      group.items.push(stockItem);
      group.childTotal += Number(stockItem.child_count || 0);
      const createdAt = stockItem.created_at
        ? new Date(stockItem.created_at).getTime()
        : 0;
      if (!group.primary || createdAt >= group.latestCreatedAt) {
        group.primary = stockItem;
        group.latestCreatedAt = createdAt;
      }
    });
    const grouped = Array.from(groups.values()).map((group) => {
      group.items.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
      const primary = group.primary || group.items[0];
      const matchChildIds = new Set();
      const matchPathIds = new Set();
      group.items.forEach((item) => {
        (item.match_child_ids || []).forEach((id) => matchChildIds.add(id));
        (item.match_path_ids || []).forEach((id) => matchPathIds.add(id));
      });
      return {
        ...primary,
        group_key: group.key,
        group_items: group.items,
        group_count: group.items.length,
        group_child_total: group.childTotal,
        group_latest_created_at: group.latestCreatedAt,
        match_child_ids: matchChildIds.size ? Array.from(matchChildIds) : undefined,
        match_path_ids: matchPathIds.size ? Array.from(matchPathIds) : undefined,
      };
    });
    grouped.sort(
      (a, b) => (b.group_latest_created_at || 0) - (a.group_latest_created_at || 0),
    );
    return grouped;
  }, [stockItems]);

  const currentVersion = itemDetail?.versions?.[0] || null;
  const currentComponentSnapshot = useMemo(
    () => normalizeComponentSnapshot(currentVersion?.components || []),
    [currentVersion],
  );
  const draftComponentSnapshot = useMemo(
    () => normalizeComponentSnapshot(draftComponents),
    [draftComponents],
  );
  const hasDraftChanges = useMemo(() => {
    const componentChanged =
      JSON.stringify(draftComponentSnapshot) !== JSON.stringify(currentComponentSnapshot);
    const metaChanged = Boolean(newVersion.label.trim() || newVersion.description.trim());
    return componentChanged || metaChanged;
  }, [draftComponentSnapshot, currentComponentSnapshot, newVersion.label, newVersion.description]);

  const currentStockFilters = () => ({
    itemId: stockFilterItemId ? stockFilterItemId : null,
    clientId: stockClientFilter ? stockClientFilter : null,
    status: stockStatusFilter ? stockStatusFilter : null,
    search: stockSearch.trim() ? stockSearch.trim() : null,
  });

  const fetchItems = async () => {
    setLoading((prev) => ({ ...prev, items: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement des items');
      }
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des items');
    } finally {
      setLoading((prev) => ({ ...prev, items: false }));
    }
  };

  const fetchItemDetail = async (itemId) => {
    if (!itemId) return;
    setLoading((prev) => ({ ...prev, detail: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items/${itemId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error("Echec du chargement de l'item");
      }
      const data = await response.json();
      setItemDetail(data);
    } catch (err) {
      flashError(err.message || "Erreur lors du chargement de l'item");
    } finally {
      setLoading((prev) => ({ ...prev, detail: false }));
    }
  };

  const fetchStock = async (filters = {}, options = {}) => {
    const { preserveUi = false } = options;
    setLoading((prev) => ({ ...prev, stock: true }));
    try {
      const params = new URLSearchParams();
      if (filters.itemId) params.set('item_id', filters.itemId);
      if (filters.clientId) params.set('client_id', filters.clientId);
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      const query = params.toString();
      const url = query
        ? `${apiBaseUrl}/inventory/stock?${query}`
        : `${apiBaseUrl}/inventory/stock`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement du stock');
      }
      const data = await response.json();
      setStockItems(Array.isArray(data) ? data : []);
      if (!preserveUi) {
        setExpandedStockKey(null);
        setExpandedStockNodes({});
        setOpenLogFor(null);
      }
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement du stock');
    } finally {
      setLoading((prev) => ({ ...prev, stock: false }));
    }
  };

  const fetchStockChildren = async (stockId) => {
    setLoadingChildren((prev) => ({ ...prev, [stockId]: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/stock/${stockId}/components`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement des sous-items');
      }
      const data = await response.json();
      setStockChildren((prev) => ({
        ...prev,
        [stockId]: Array.isArray(data) ? data : [],
      }));
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des sous-items');
    } finally {
      setLoadingChildren((prev) => ({ ...prev, [stockId]: false }));
    }
  };

  const fetchCatalogVersionComponents = async (versionId) => {
    if (!versionId) return [];
    if (hasOwn(catalogVersionComponents, versionId) || catalogVersionLoading[versionId]) {
      return catalogVersionComponents[versionId] || [];
    }
    setCatalogVersionLoading((prev) => ({ ...prev, [versionId]: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/versions/${versionId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement des sous-items');
      }
      const data = await response.json();
      const components = Array.isArray(data?.components) ? data.components : [];
      setCatalogVersionComponents((prev) => ({ ...prev, [versionId]: components }));
      return components;
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des sous-items');
      setCatalogVersionComponents((prev) => ({ ...prev, [versionId]: [] }));
      return [];
    } finally {
      setCatalogVersionLoading((prev) => ({ ...prev, [versionId]: false }));
    }
  };

  const fetchLatestVersionForItem = async (itemId) => {
    if (!itemId) return null;
    if (hasOwn(catalogLatestVersionMap, itemId)) {
      return catalogLatestVersionMap[itemId];
    }
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items/${itemId}/versions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement des versions');
      }
      const data = await response.json();
      const latest = Array.isArray(data) && data.length > 0 ? data[0] : null;
      const latestId = latest ? latest.id : null;
      setCatalogLatestVersionMap((prev) => ({ ...prev, [itemId]: latestId }));
      return latestId;
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des versions');
      setCatalogLatestVersionMap((prev) => ({ ...prev, [itemId]: null }));
      return null;
    }
  };

  const collectCompatibleItemIds = async (versionId, visited = new Set()) => {
    if (!versionId || visited.has(versionId)) return new Set();
    visited.add(versionId);
    const components = await fetchCatalogVersionComponents(versionId);
    const itemIds = new Set();
    for (const component of components) {
      if (!component?.child_item_id) continue;
      itemIds.add(component.child_item_id);
      let childVersionId = component.child_version_id;
      if (!childVersionId) {
        childVersionId = await fetchLatestVersionForItem(component.child_item_id);
      }
      if (childVersionId && !visited.has(childVersionId)) {
        const nested = await collectCompatibleItemIds(childVersionId, visited);
        nested.forEach((id) => itemIds.add(id));
      }
    }
    return itemIds;
  };

  const resolveCatalogComponentVersionId = async (component) => {
    if (!component) return null;
    const componentId = component.id;
    if (hasOwn(catalogResolvedComponentVersions, componentId)) {
      return catalogResolvedComponentVersions[componentId];
    }
    let versionId = component.child_version_id || null;
    if (!versionId && component.child_item_id) {
      versionId = await fetchLatestVersionForItem(component.child_item_id);
    }
    setCatalogResolvedComponentVersions((prev) => ({
      ...prev,
      [componentId]: versionId || null,
    }));
    return versionId || null;
  };

  const fetchLinkableStockItems = async () => {
    setLoadingLinkable(true);
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/stock`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement du stock');
      }
      const data = await response.json();
      setLinkableStockItems(Array.isArray(data) ? data : []);
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement du stock');
    } finally {
      setLoadingLinkable(false);
    }
  };

  const fetchStockFormVersions = async (itemId) => {
    if (!itemId) {
      setStockFormVersions([]);
      return;
    }
    setLoading((prev) => ({ ...prev, stockVersions: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items/${itemId}/versions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement des versions');
      }
      const data = await response.json();
      const versions = Array.isArray(data) ? data : [];
      setStockFormVersions(versions);
      if (versions.length > 0) {
        setNewStock((prev) => ({ ...prev, item_version_id: versions[0].id }));
      }
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des versions');
    } finally {
      setLoading((prev) => ({ ...prev, stockVersions: false }));
    }
  };

  const fetchClientsAndProjects = async () => {
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        fetch(`${apiBaseUrl}/clients`, { headers: getAuthHeaders() }),
        fetch(`${apiBaseUrl}/projects`, { headers: getAuthHeaders() }),
      ]);
      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(Array.isArray(clientsData) ? clientsData : []);
      }
      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(Array.isArray(projectsData) ? projectsData : []);
      }
    } catch (err) {
      flashError('Erreur lors du chargement des proprietaires/projets');
    }
  };

  const validateDraftComponents = (components, level = 1) => {
    for (const component of components) {
      if (component.type === 'existing') {
        if (!component.child_item_id) {
          return `Choisissez un item pour les sous-items (niveau ${level}).`;
        }
      } else {
        const name = component.new_item?.name?.trim();
        if (!name) {
          return `Nom requis pour les sous-items crees (niveau ${level}).`;
        }
        const nestedError = validateDraftComponents(
          component.new_item?.components || [],
          level + 1,
        );
        if (nestedError) return nestedError;
      }
    }
    return null;
  };

  const shouldCreateVersionForDraft = (draft) => {
    return draft.type === 'new';
  };

  const createItemFromDraft = async (draft, createdItems) => {
    if (draft.type === 'existing') {
      return {
        itemId: toOptionalNumber(draft.child_item_id),
        versionId: toOptionalNumber(draft.child_version_id),
      };
    }
    const response = await fetch(`${apiBaseUrl}/inventory/items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        name: draft.new_item.name.trim(),
        description: draft.new_item.description || null,
        purchase_url: draft.new_item.purchase_url || null,
      }),
    });
    if (!response.ok) {
      throw new Error("Impossible d'ajouter le sous-item");
    }
    const created = await response.json();
    if (Array.isArray(createdItems)) {
      createdItems.push(created);
    }
    let versionId = null;
    if (shouldCreateVersionForDraft(draft)) {
      const versionResponse = await fetch(
        `${apiBaseUrl}/inventory/items/${created.id}/versions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            label: draft.new_item.version?.label?.trim() || null,
            description: draft.new_item.version?.description?.trim() || null,
            base_version_id: null,
          }),
        },
      );
      if (!versionResponse.ok) {
        throw new Error("Impossible de creer la version du sous-item");
      }
      const versionPayload = await versionResponse.json();
      versionId = versionPayload.id;
    }
    if (versionId && draft.new_item?.components?.length) {
      for (const component of draft.new_item.components) {
        const child = await createItemFromDraft(component, createdItems);
        const compResponse = await fetch(
          `${apiBaseUrl}/inventory/versions/${versionId}/components`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({
              child_item_id: toOptionalNumber(child.itemId),
              child_version_id: toOptionalNumber(child.versionId),
              quantity: Math.max(1, Number(component.quantity) || 1),
              notes: component.notes?.trim() || null,
            }),
          },
        );
        if (!compResponse.ok) {
          throw new Error("Impossible d'ajouter un sous-item");
        }
        await compResponse.json();
      }
    }
    return { itemId: created.id, versionId };
  };

  const handleCreateItem = async (event) => {
    event.preventDefault();
    if (!newItem.name.trim()) return;
    const draftError = validateDraftComponents(newItemComponents);
    if (draftError) {
      flashError(draftError);
      return;
    }
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          name: newItem.name,
          description: newItem.description || null,
          purchase_url: newItem.purchase_url || null,
        }),
      });
      if (!response.ok) {
        throw new Error("Impossible d'ajouter l'item");
      }
      const created = await response.json();
      let versionId = null;
      const versionResponse = await fetch(
        `${apiBaseUrl}/inventory/items/${created.id}/versions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            label: newItemVersion.label.trim() || null,
            description: newItemVersion.description.trim() || null,
            base_version_id: null,
          }),
        },
      );
      if (!versionResponse.ok) {
        throw new Error("Impossible de creer la version initiale");
      }
      const versionPayload = await versionResponse.json();
      versionId = versionPayload.id;
      const createdChildren = [];
      if (versionId && newItemComponents.length > 0) {
        for (const component of newItemComponents) {
          const child = await createItemFromDraft(component, createdChildren);
          const compResponse = await fetch(
            `${apiBaseUrl}/inventory/versions/${versionId}/components`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                child_item_id: toOptionalNumber(child.itemId),
                child_version_id: toOptionalNumber(child.versionId),
                quantity: Math.max(1, Number(component.quantity) || 1),
                notes: component.notes?.trim() || null,
              }),
            },
          );
          if (!compResponse.ok) {
            throw new Error("Impossible d'ajouter un sous-item");
          }
          await compResponse.json();
        }
      }
      setItems((prev) => [...prev, created, ...createdChildren]);
      setNewItem({ name: '', description: '', purchase_url: '' });
      setNewItemVersion({ label: '', description: '' });
      setNewItemComponents([]);
      setNewItemComponentVersionMap({});
      setNewItemComponentVersionLoading({});
      setCatalogItemId(created.id);
      setShowNewItemModal(false);
      if (versionId) {
        await fetchItemDetail(created.id);
      }
      flashSuccess('Item cree');
    } catch (err) {
      flashError(err.message || "Erreur lors de la creation de l'item");
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items/${editingItem.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          name: editingItem.name,
          description: editingItem.description,
          purchase_url: editingItem.purchase_url || null,
        }),
      });
      if (!response.ok) {
        throw new Error("Impossible de mettre a jour l'item");
      }
      const updated = await response.json();
      setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setItemDetail((prev) => (prev ? { ...prev, item: updated } : prev));
      flashSuccess('Item mis a jour');
    } catch (err) {
      flashError(err.message || "Erreur lors de la mise a jour de l'item");
    }
  };

  const handleDeleteItem = async () => {
    if (!itemDetail?.item?.id) return;
    if (!window.confirm('Supprimer cet item et toutes ses versions ?')) return;
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items/${itemDetail.item.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error("Impossible de supprimer l'item");
      }
      setItems((prev) => prev.filter((item) => item.id !== itemDetail.item.id));
      setCatalogItemId(null);
      setItemDetail(null);
      flashSuccess('Item supprime');
    } catch (err) {
      flashError(err.message || "Erreur lors de la suppression de l'item");
    }
  };

  const handleCreateVersion = async (event) => {
    event.preventDefault();
    if (!itemDetail?.item?.id) return;
    if (!hasDraftChanges) {
      flashError('Aucun changement a enregistrer');
      return;
    }
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventory/items/${itemDetail.item.id}/versions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            label: newVersion.label || null,
            description: newVersion.description || null,
            base_version_id: null,
          }),
        },
      );
      if (!response.ok) {
        throw new Error('Impossible de creer la version');
      }
      const createdVersion = await response.json();
      if (draftComponents.length > 0) {
        for (const component of draftComponents) {
          const compResponse = await fetch(
            `${apiBaseUrl}/inventory/versions/${createdVersion.id}/components`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                child_item_id: toOptionalNumber(component.child_item_id),
                child_version_id: toOptionalNumber(component.child_version_id),
                quantity: Number(component.quantity) || 1,
                notes: component.notes || null,
              }),
            },
          );
          if (!compResponse.ok) {
            throw new Error("Impossible d'ajouter un sous-item");
          }
          await compResponse.json();
        }
      }
      setNewVersion({ label: '', description: '' });
      await fetchItemDetail(itemDetail.item.id);
      flashSuccess('Version creee');
    } catch (err) {
      flashError(err.message || 'Erreur lors de la creation de la version');
    }
  };

  const fetchComponentVersions = async (itemId) => {
    if (!itemId) return [];
    const key = String(itemId);
    if (componentVersionMap[key]) return componentVersionMap[key];
    if (componentVersionLoading[key]) return [];
    setComponentVersionLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items/${itemId}/versions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement des versions');
      }
      const data = await response.json();
      const versions = Array.isArray(data) ? data : [];
      setComponentVersionMap((prev) => ({ ...prev, [key]: versions }));
      return versions;
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des versions');
      return [];
    } finally {
      setComponentVersionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleComponentItemChange = async (value) => {
    setComponentForm((prev) => ({ ...prev, child_item_id: value, child_version_id: '' }));
    if (!value) {
      setComponentVersions([]);
      return;
    }
    setComponentLoading(true);
    try {
      const versions = await fetchComponentVersions(value);
      setComponentVersions(versions);
      if (versions.length > 0) {
        setComponentForm((prev) => ({
          ...prev,
          child_version_id: String(versions[0].id),
        }));
      }
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des versions');
    } finally {
      setComponentLoading(false);
    }
  };

  const fetchNewItemComponentVersions = async (itemId) => {
    if (!itemId) return [];
    const key = String(itemId);
    if (newItemComponentVersionMap[key]) return newItemComponentVersionMap[key];
    if (newItemComponentVersionLoading[key]) return [];
    setNewItemComponentVersionLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/items/${itemId}/versions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement des versions');
      }
      const data = await response.json();
      const versions = Array.isArray(data) ? data : [];
      setNewItemComponentVersionMap((prev) => ({ ...prev, [key]: versions }));
      return versions;
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement des versions');
      return [];
    } finally {
      setNewItemComponentVersionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleAddNewItemComponent = (parentId, type) => {
    setNewItemComponents((prev) =>
      addComponentToTree(prev, parentId, createDraftComponent(type)),
    );
  };

  const handleUpdateNewItemComponent = (componentId, updater) => {
    setNewItemComponents((prev) => updateComponentInTree(prev, componentId, updater));
  };

  const handleRemoveNewItemComponent = (componentId) => {
    setNewItemComponents((prev) => removeComponentFromTree(prev, componentId));
  };

  const handleNewItemComponentItemChange = async (componentId, value) => {
    handleUpdateNewItemComponent(componentId, (component) => ({
      ...component,
      child_item_id: value,
      child_version_id: '',
    }));
    if (!value) return;
    const versions = await fetchNewItemComponentVersions(value);
    if (versions.length > 0) {
      handleUpdateNewItemComponent(componentId, (component) => ({
        ...component,
        child_version_id: String(versions[0].id),
      }));
    }
  };

  const handleAddComponent = (event) => {
    event.preventDefault();
    if (!componentForm.child_item_id) return;
    const quantity = Math.max(1, Number(componentForm.quantity) || 1);
    const childItem = items.find(
      (item) => String(item.id) === String(componentForm.child_item_id),
    );
    const childVersion = componentVersions.find(
      (version) => String(version.id) === String(componentForm.child_version_id),
    );
    setDraftComponents((prev) => [
      ...prev,
      {
        id: `draft-${componentForm.child_item_id}-${componentForm.child_version_id}-${Date.now()}`,
        child_item_id: componentForm.child_item_id,
        child_version_id: componentForm.child_version_id,
        quantity,
        notes: componentForm.notes || '',
        child_item_name: childItem?.name || '',
        child_version_number: childVersion?.version_number || null,
        child_version_label: childVersion?.label || '',
      },
    ]);
    setComponentForm({
      child_item_id: '',
      child_version_id: '',
      quantity: 1,
      notes: '',
    });
    setComponentVersions([]);
  };

  const handleDeleteComponent = (componentId) => {
    setDraftComponents((prev) => prev.filter((component) => component.id !== componentId));
  };

  const updateDraftComponent = (componentId, updates) => {
    setDraftComponents((prev) =>
      prev.map((component) =>
        component.id === componentId ? { ...component, ...updates } : component,
      ),
    );
  };

  const resetDraftChanges = () => {
    setDraftComponents(buildDraftComponentsFromVersion(currentVersion));
    setNewVersion({ label: '', description: '' });
    setComponentForm({
      child_item_id: '',
      child_version_id: '',
      quantity: 1,
      notes: '',
    });
    setComponentVersions([]);
  };

  const handleCreateStock = async (event) => {
    event.preventDefault();
    if (!newStock.item_version_id) return;
    const quantity = Math.max(1, Number(newStock.quantity) || 1);
    if (!newStock.create_children && selectedChildStockIds.length > 0) {
      flashError('Impossible de lier des sous-items si la creation est desactivee.');
      return;
    }
    if (selectedChildStockIds.length > 0 && quantity > 1) {
      flashError('La quantite doit etre 1 si vous liez des sous-items existants.');
      return;
    }
    try {
      for (let index = 0; index < quantity; index += 1) {
        const response = await fetch(`${apiBaseUrl}/inventory/stock`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            item_version_id: toOptionalNumber(newStock.item_version_id),
            occurrence_name: newStock.occurrence_name || null,
            status: newStock.status,
            client_id: toOptionalNumber(newStock.client_id),
            project_id: toOptionalNumber(newStock.project_id),
            location: newStock.location || null,
            tracking_number:
              newStock.status === 'ordered' ? newStock.tracking_number || null : null,
            ordered_at: normalizeDateTime(newStock.ordered_at),
            received_at: normalizeDateTime(newStock.received_at),
            shipped_at: normalizeDateTime(newStock.shipped_at),
            removed_at: normalizeDateTime(newStock.removed_at),
            notes: newStock.notes || null,
            child_stock_item_ids: selectedChildStockIds,
            create_children: newStock.create_children,
          }),
        });
        if (!response.ok) {
          throw new Error("Impossible d'ajouter l'occurrence");
        }
        await response.json();
      }
      await fetchStock(currentStockFilters());
      setNewStock((prev) => ({
        ...prev,
        occurrence_name: '',
        location: '',
        tracking_number: '',
        ordered_at: '',
        received_at: '',
        shipped_at: '',
        removed_at: '',
        notes: '',
        quantity: 1,
        create_children: true,
      }));
      setSelectedChildStockIds([]);
      setLinkableSearch('');
      setShowExistingStockModal(false);
      flashSuccess(quantity > 1 ? `${quantity} occurrences creees` : 'Occurrence creee');
    } catch (err) {
      flashError(err.message || "Erreur lors de la creation de l'occurrence");
    }
  };

  const handleQuickStockCreate = async (event) => {
    event.preventDefault();
    const name = quickStock.item_name.trim();
    if (!name) return;
    const quantity = Math.max(1, Number(quickStock.quantity) || 1);
    setLoading((prev) => ({ ...prev, quick: true }));
    try {
      const normalized = name.toLowerCase();
      const existingItem = items.find((item) => item.name.toLowerCase() === normalized);
      let itemId = existingItem?.id || null;

      if (!itemId) {
        const itemResponse = await fetch(`${apiBaseUrl}/inventory/items`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            name: name,
            description: quickStock.description || null,
          }),
        });
        if (!itemResponse.ok) {
          throw new Error("Impossible de creer l'item");
        }
        const createdItem = await itemResponse.json();
        itemId = createdItem.id;
        setItems((prev) => [...prev, createdItem]);
      }

      let versionId = null;
      const versionLabel = quickStock.version_label.trim();
      if (versionLabel || !existingItem) {
        const versionResponse = await fetch(
          `${apiBaseUrl}/inventory/items/${itemId}/versions`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({
              label: versionLabel || null,
              description: null,
              base_version_id: null,
            }),
          },
        );
        if (!versionResponse.ok) {
          throw new Error("Impossible de creer la version");
        }
        const versionPayload = await versionResponse.json();
        versionId = versionPayload.id;
      } else {
        const versionsResponse = await fetch(
          `${apiBaseUrl}/inventory/items/${itemId}/versions`,
          { headers: getAuthHeaders() },
        );
        if (!versionsResponse.ok) {
          throw new Error('Impossible de charger les versions');
        }
        const versionsData = await versionsResponse.json();
        const versions = Array.isArray(versionsData) ? versionsData : [];
        if (versions.length > 0) {
          versionId = versions[0].id;
        } else {
          const versionResponse = await fetch(
            `${apiBaseUrl}/inventory/items/${itemId}/versions`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                label: null,
                description: null,
                base_version_id: null,
              }),
            },
          );
          if (!versionResponse.ok) {
            throw new Error("Impossible de creer la version");
          }
          const versionPayload = await versionResponse.json();
          versionId = versionPayload.id;
        }
      }

      for (let index = 0; index < quantity; index += 1) {
        const stockResponse = await fetch(`${apiBaseUrl}/inventory/stock`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            item_version_id: versionId,
            occurrence_name: quickStock.occurrence_name || null,
            status: quickStock.status,
            client_id: toOptionalNumber(quickStock.client_id),
            project_id: toOptionalNumber(quickStock.project_id),
            location: quickStock.location || null,
            tracking_number:
              quickStock.status === 'ordered' ? quickStock.tracking_number || null : null,
            notes: quickStock.notes || null,
          }),
        });
        if (!stockResponse.ok) {
          throw new Error("Impossible d'ajouter au stock");
        }
        await stockResponse.json();
      }
      setStockFilterItemId(itemId ? String(itemId) : '');
      await fetchStock({
        itemId: itemId ? String(itemId) : null,
        clientId: stockClientFilter || null,
        status: stockStatusFilter || null,
        search: stockSearch.trim() || null,
      });
      setQuickStock({
        item_name: '',
        description: '',
        version_label: '',
        occurrence_name: '',
        status: 'ordered',
        client_id: '',
        project_id: '',
        location: '',
        tracking_number: '',
        notes: '',
        quantity: 1,
      });
      setShowQuickStockModal(false);
      flashSuccess(quantity > 1 ? `${quantity} occurrences ajoutees` : 'Stock ajoute');
    } catch (err) {
      flashError(err.message || 'Erreur lors de la creation rapide');
    } finally {
      setLoading((prev) => ({ ...prev, quick: false }));
    }
  };

  const handleToggleExpand = (groupKey, primaryId, groupCount) => {
    setExpandedStockKey((prev) => {
      const next = prev === groupKey ? null : groupKey;
      if (next === null) {
        setOpenLogFor(null);
        setExpandedStockNodes({});
      } else if (groupCount === 1 && primaryId) {
        fetchStockChildren(primaryId);
      }
      return next;
    });
  };

  const handleToggleStockNode = (stockId) => {
    const isExpanded = Boolean(expandedStockNodes[stockId]);
    if (!isExpanded) {
      fetchStockChildren(stockId);
    }
    setExpandedStockNodes((prev) => {
      const next = { ...prev };
      if (isExpanded) {
        delete next[stockId];
      } else {
        next[stockId] = true;
      }
      return next;
    });
  };

  const setStockEditsFromItem = (stockItem) => {
    setStockEdits({
      occurrence_name: stockItem.occurrence_name || '',
      status: stockItem.status,
      client_id: stockItem.client_id || '',
      project_id: stockItem.project_id || '',
      location: stockItem.location || '',
      tracking_number: stockItem.tracking_number || '',
      ordered_at: toDateTimeLocal(stockItem.ordered_at),
      received_at: toDateTimeLocal(stockItem.received_at),
      shipped_at: toDateTimeLocal(stockItem.shipped_at),
      removed_at: toDateTimeLocal(stockItem.removed_at),
      notes: stockItem.notes || '',
    });
  };

  const openStockEditModal = (stockItem) => {
    setEditingStockItem(stockItem);
    setStockEditsFromItem(stockItem);
    setShowStockEditModal(true);
  };

  const closeStockEditModal = () => {
    setShowStockEditModal(false);
    setEditingStockItem(null);
  };

  const handleUpdateStock = async (stockId) => {
    const scrollY = window.scrollY;
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/stock/${stockId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          occurrence_name: stockEdits.occurrence_name || null,
          status: stockEdits.status,
          client_id: toOptionalNumber(stockEdits.client_id),
          project_id: toOptionalNumber(stockEdits.project_id),
          location: stockEdits.location || null,
          tracking_number:
            stockEdits.status === 'ordered' ? stockEdits.tracking_number || null : null,
          ordered_at: normalizeDateTime(stockEdits.ordered_at),
          received_at: normalizeDateTime(stockEdits.received_at),
          shipped_at: normalizeDateTime(stockEdits.shipped_at),
          removed_at: normalizeDateTime(stockEdits.removed_at),
          notes: stockEdits.notes || null,
        }),
      });
      if (!response.ok) {
        throw new Error("Impossible de mettre a jour l'occurrence");
      }
      await response.json();
      await fetchStock(currentStockFilters());
      restoreScroll(scrollY);
      await refreshStockLogs(stockId);
      setEditingStockItem(null);
      setShowStockEditModal(false);
      flashSuccess('Occurrence mise a jour');
    } catch (err) {
      restoreScroll(scrollY);
      flashError(err.message || "Erreur lors de la mise a jour de l'occurrence");
    }
  };

  const applyInlineStockUpdate = (stockId, fields) => {
    if (!stockId || !fields) return;
    const project =
      fields.project_id !== undefined
        ? projects.find(
            (entry) => String(entry.id) === String(fields.project_id || ''),
          )
        : null;
    const projectName = project ? project.name : null;
    setStockItems((prev) =>
      prev.map((item) => {
        if (item.id !== stockId) return item;
        const next = { ...item, ...fields };
        if (fields.project_id !== undefined) {
          next.project_name = projectName;
        }
        return next;
      }),
    );
    setStockChildren((prev) => {
      let hasChanges = false;
      let descendantIds = null;
      if (fields.project_id !== undefined) {
        descendantIds = new Set();
        const stack = [stockId];
        while (stack.length > 0) {
          const current = stack.pop();
          const children = prev[current] || [];
          children.forEach((child) => {
            if (!child?.id || descendantIds.has(child.id)) return;
            descendantIds.add(child.id);
            stack.push(child.id);
          });
        }
      }
      const next = {};
      Object.entries(prev).forEach(([parentId, children]) => {
        let updated = children;
        if (Array.isArray(children)) {
          let childUpdated = false;
          updated = children.map((child) => {
            const shouldUpdate =
              child.id === stockId ||
              (descendantIds && descendantIds.has(child.id));
            if (!shouldUpdate) return child;
            childUpdated = true;
            const nextChild =
              descendantIds && descendantIds.has(child.id)
                ? { ...child, project_id: fields.project_id }
                : { ...child, ...fields };
            if (fields.project_id !== undefined) {
              nextChild.project_name = projectName;
            }
            return nextChild;
          });
          if (childUpdated) {
            hasChanges = true;
          }
        }
        next[parentId] = updated;
      });
      return hasChanges ? next : prev;
    });
  };

  const handleInlineStockUpdate = async (stockId, fields) => {
    if (!stockId || !fields) return;
    const scrollY = window.scrollY;
    try {
      const normalized = {};
      if (Object.prototype.hasOwnProperty.call(fields, 'occurrence_name')) {
        normalized.occurrence_name = fields.occurrence_name || null;
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'status')) {
        normalized.status = fields.status;
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'location')) {
        normalized.location = fields.location || null;
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'notes')) {
        normalized.notes = fields.notes || null;
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'project_id')) {
        normalized.project_id = toOptionalNumber(fields.project_id);
      }
      applyInlineStockUpdate(stockId, normalized);
      const payload = {
        ...normalized,
      };
      if (normalized.status !== undefined && normalized.status !== 'ordered') {
        payload.tracking_number = null;
        applyInlineStockUpdate(stockId, { tracking_number: null });
      }
      const response = await fetch(`${apiBaseUrl}/inventory/stock/${stockId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Impossible de mettre a jour l'occurrence");
      }
      await response.json();
      await fetchStock(currentStockFilters(), { preserveUi: true });
      restoreScroll(scrollY);
      await refreshStockLogs(stockId);
      flashSuccess('Occurrence mise a jour');
    } catch (err) {
      await fetchStock(currentStockFilters(), { preserveUi: true });
      restoreScroll(scrollY);
      flashError(err.message || "Erreur lors de la mise a jour de l'occurrence");
    }
  };

  const handleDeleteStock = async (stockItem) => {
    if (!stockItem?.id) return;
    const hasChildren = Boolean(stockItem.child_count);
    const message = hasChildren
      ? 'Supprimer cette occurrence et ses sous-items ?'
      : 'Supprimer cette occurrence ?';
    if (!window.confirm(message)) return;
    try {
      if (openLogFor === stockItem.id) {
        setOpenLogFor(null);
      }
      if (expandedStockNodes[stockItem.id]) {
        setExpandedStockNodes((prev) => {
          const next = { ...prev };
          delete next[stockItem.id];
          return next;
        });
      }
      const response = await fetch(`${apiBaseUrl}/inventory/stock/${stockItem.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error("Impossible de supprimer l'occurrence");
      }
      await fetchStock(currentStockFilters());
      setEditingStockItem(null);
      setShowStockEditModal(false);
      flashSuccess('Occurrence supprimee');
    } catch (err) {
      flashError(err.message || "Erreur lors de la suppression de l'occurrence");
    }
  };

  const handleLinkStockChild = async (parentId, childId) => {
    if (!parentId || !childId) return;
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/stock/${parentId}/children`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ child_stock_item_id: childId }),
      });
      if (!response.ok) {
        throw new Error("Impossible de lier l'occurrence");
      }
      await fetchStockChildren(parentId);
      await fetchStock(currentStockFilters(), { preserveUi: true });
      await refreshStockLogs(parentId, [childId]);
      flashSuccess('Occurrence liee');
      return true;
    } catch (err) {
      flashError(err.message || "Erreur lors du lien de l'occurrence");
      return false;
    }
  };

  const handleUnlinkStockChild = async (parentId, childId) => {
    if (!parentId || !childId) return;
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventory/stock/${parentId}/children/${childId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        },
      );
      if (!response.ok) {
        throw new Error("Impossible de delier l'occurrence");
      }
      await fetchStockChildren(parentId);
      await fetchStock(currentStockFilters(), { preserveUi: true });
      await refreshStockLogs(parentId, [childId]);
      flashSuccess('Occurrence deliee');
      return true;
    } catch (err) {
      flashError(err.message || "Erreur lors de la deliaison");
      return false;
    }
  };

  const handleCreateLinkedChild = async (parentItem, slot) => {
    if (!parentItem?.id || !slot?.item_id) return;
    try {
      let versionId = slot.item_version_id;
      if (!versionId) {
        versionId = await fetchLatestVersionForItem(slot.item_id);
      }
      if (!versionId) {
        throw new Error('Version enfant introuvable');
      }
      const response = await fetch(`${apiBaseUrl}/inventory/stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          item_version_id: versionId,
          status: parentItem.status,
          client_id: parentItem.client_id || null,
          project_id: parentItem.project_id || null,
          location: parentItem.location || null,
          tracking_number:
            parentItem.status === 'ordered' ? parentItem.tracking_number || null : null,
          ordered_at: normalizeDateTime(parentItem.ordered_at),
          received_at: normalizeDateTime(parentItem.received_at),
          shipped_at: normalizeDateTime(parentItem.shipped_at),
          removed_at: normalizeDateTime(parentItem.removed_at),
          notes: null,
          create_children: true,
          child_stock_item_ids: [],
        }),
      });
      if (!response.ok) {
        throw new Error("Impossible de creer l'occurrence");
      }
      const created = await response.json();
      const linked = await handleLinkStockChild(parentItem.id, created.id);
      return linked;
    } catch (err) {
      flashError(err.message || "Erreur lors de la creation de l'occurrence");
      return false;
    }
  };

  const handleLoadLogs = async (stockId) => {
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/stock/${stockId}/logs`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Echec du chargement du journal');
      }
      const data = await response.json();
      setStockLogs((prev) => ({ ...prev, [stockId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      flashError(err.message || 'Erreur lors du chargement du journal');
    }
  };

  const handleAddLog = async (stockId) => {
    const message = newLogMessage[stockId];
    if (!message || !message.trim()) return;
    try {
      const response = await fetch(`${apiBaseUrl}/inventory/stock/${stockId}/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ message }),
      });
      if (!response.ok) {
        throw new Error('Impossible d ajouter la note');
      }
      await response.json();
      setNewLogMessage((prev) => ({ ...prev, [stockId]: '' }));
      await handleLoadLogs(stockId);
      flashSuccess('Note ajoutee');
    } catch (err) {
      flashError(err.message || 'Erreur lors de la creation du journal');
    }
  };

  const handleUpdateLog = async (stockId, logId, updates) => {
    if (!stockId || !logId || !updates) return false;
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventory/stock/${stockId}/logs/${logId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify(updates),
        },
      );
      if (!response.ok) {
        throw new Error('Impossible de mettre a jour la note');
      }
      const updated = await response.json();
      setStockLogs((prev) => ({
        ...prev,
        [stockId]: (prev[stockId] || [])
          .map((log) => (log.id === logId ? updated : log))
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          ),
      }));
      flashSuccess('Note mise a jour');
      return true;
    } catch (err) {
      flashError(err.message || 'Erreur lors de la mise a jour de la note');
      return false;
    }
  };

  const handleDeleteLog = async (stockId, logId) => {
    if (!stockId || !logId) return false;
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventory/stock/${stockId}/logs/${logId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        },
      );
      if (!response.ok) {
        throw new Error('Impossible de supprimer la note');
      }
      setStockLogs((prev) => ({
        ...prev,
        [stockId]: (prev[stockId] || []).filter((log) => log.id !== logId),
      }));
      flashSuccess('Note supprimee');
      return true;
    } catch (err) {
      flashError(err.message || 'Erreur lors de la suppression de la note');
      return false;
    }
  };

  const handleRefresh = async () => {
    await fetchItems();
    if (activeTab === 'catalog' && catalogItemId) {
      await fetchItemDetail(catalogItemId);
    }
    if (activeTab === 'stock') {
      await fetchStock(currentStockFilters());
    }
  };

  const handleToggleCatalogComponent = async (component, ancestorVersionIds = []) => {
    if (!component?.id) return;
    const componentId = component.id;
    if (catalogExpandedComponents[componentId]) {
      setCatalogExpandedComponents((prev) => {
        const next = { ...prev };
        delete next[componentId];
        return next;
      });
      return;
    }

    const versionId = await resolveCatalogComponentVersionId(component);
    if (!versionId) {
      setCatalogComponentWarnings((prev) => ({
        ...prev,
        [componentId]: 'Aucune version disponible.',
      }));
      setCatalogExpandedComponents((prev) => ({ ...prev, [componentId]: true }));
      return;
    }

    if (ancestorVersionIds.includes(versionId)) {
      setCatalogComponentWarnings((prev) => ({
        ...prev,
        [componentId]: 'Boucle detectee.',
      }));
      setCatalogExpandedComponents((prev) => ({ ...prev, [componentId]: true }));
      return;
    }

    setCatalogComponentWarnings((prev) => {
      if (!prev[componentId]) return prev;
      const next = { ...prev };
      delete next[componentId];
      return next;
    });
    setCatalogExpandedComponents((prev) => ({ ...prev, [componentId]: true }));
    await fetchCatalogVersionComponents(versionId);
  };

  const renderStockChildrenList = (parentId, depth = 0, highlightIds = new Set()) => {
    if (loadingChildren[parentId]) {
      return <div className="mt-2 text-[11px] text-slate-400">Chargement...</div>;
    }
    const children = stockChildren[parentId] || [];
    if (children.length === 0) {
      return <div className="mt-2 text-[11px] text-slate-400">Aucun sous-item.</div>;
    }
    return (
      <div
        className={`mt-2 space-y-2 ${
          depth > 0 ? 'border-l border-slate-200/70 pl-4' : ''
        }`}
      >
        {children.map((child) => {
          const isExpanded = Boolean(expandedStockNodes[child.id]);
          const hasChildren = Boolean(child.child_count);
          const rowClass =
            depth > 0
              ? 'rounded-lg border border-slate-200/70 bg-white px-3 py-2'
              : 'rounded-xl border border-slate-200/80 bg-slate-50/60 px-3 py-2';
          const isMatch = highlightIds.has(child.id);
          return (
            <div key={child.id} className={`${rowClass} ${isMatch ? 'ring-2 ring-amber-300' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-slate-800">
                    {child.serial_number || `#${child.id}`} - {child.item_name}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {child.client_name ? `Proprietaire: ${child.client_name}` : ''}
                    {child.location ? ` - ${child.location}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-1 text-[10px] font-semibold ${
                      statusClassMap[child.status] || 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {getStatusLabel(child.status)}
                  </span>
                  {hasChildren && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStockNode(child.id);
                      }}
                      className="rounded-full border border-transparent px-2 py-1 text-[10px] text-slate-600 transition hover:border-slate-200 hover:bg-slate-50"
                    >
                      {isExpanded ? 'Masquer' : `Sous-items (${child.child_count})`}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openQrModal(child);
                    }}
                    className="rounded-full border border-transparent px-2 py-1 text-[10px] text-slate-600 transition hover:border-slate-200 hover:bg-slate-50"
                  >
                    QR
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openStockEditModal(child);
                    }}
                    className="rounded-full border border-transparent px-2.5 py-1 text-[11px] text-blue-600 transition hover:border-blue-100 hover:bg-blue-50"
                  >
                    Editer
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteStock(child);
                    }}
                    className="rounded-full border border-transparent px-2.5 py-1 text-[11px] text-rose-600 transition hover:border-rose-200 hover:bg-rose-50"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
              {isExpanded && (
                <div className="mt-2">
                      {renderStockChildrenList(child.id, depth + 1, highlightIds)}
                    </div>
                  )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCatalogComponents = (components, depth = 0, ancestorVersionIds = []) => {
    if (!Array.isArray(components) || components.length === 0) {
      return <div className="text-slate-400">Aucun sous-item</div>;
    }
    return (
      <div
        className={`space-y-2 ${
          depth > 0 ? 'border-l border-slate-200/70 pl-4' : ''
        }`}
      >
        {components.map((component) => {
          const componentId = component.id;
          const isExpanded = Boolean(catalogExpandedComponents[componentId]);
          const resolvedVersionId = catalogResolvedComponentVersions[componentId];
          const hasResolved = hasOwn(catalogResolvedComponentVersions, componentId);
          const warning = catalogComponentWarnings[componentId];
          const versionLabel = component.child_version_number
            ? ` - V${component.child_version_number} ${component.child_version_label || ''}`
            : '';
          return (
            <div
              key={component.id}
              className="rounded-lg border border-slate-200/70 bg-white px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-slate-800">
                    {component.child_item_name || `Item #${component.child_item_id}`}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Qte: {component.quantity}
                    {versionLabel}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleCatalogComponent(component, ancestorVersionIds);
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-1 text-[10px] text-slate-600 transition hover:border-slate-200 hover:bg-slate-50"
                >
                  <ChevronRight
                    className={`h-3 w-3 transition-transform ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                  />
                  {isExpanded ? 'Masquer' : 'Sous-items'}
                </button>
              </div>
              {isExpanded && (
                <div className="mt-2 text-[11px] text-slate-500">
                  {warning ? (
                    <div className="text-amber-600">{warning}</div>
                  ) : !hasResolved ? (
                    <div className="text-slate-400">Chargement...</div>
                  ) : resolvedVersionId && catalogVersionLoading[resolvedVersionId] ? (
                    <div className="text-slate-400">Chargement...</div>
                  ) : resolvedVersionId ? (
                    renderCatalogComponents(
                      catalogVersionComponents[resolvedVersionId] || [],
                      depth + 1,
                      [...ancestorVersionIds, resolvedVersionId],
                    )
                  ) : (
                    <div className="text-slate-400">Aucun sous-item.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!itemDetail?.versions) {
      setCatalogVersionComponents({});
      setCatalogExpandedComponents({});
      setCatalogResolvedComponentVersions({});
      setCatalogLatestVersionMap({});
      setCatalogComponentWarnings({});
      return;
    }
    const next = {};
    itemDetail.versions.forEach((version) => {
      next[version.id] = Array.isArray(version.components) ? version.components : [];
    });
    setCatalogVersionComponents(next);
    setCatalogExpandedComponents({});
    setCatalogResolvedComponentVersions({});
    setCatalogLatestVersionMap({});
    setCatalogComponentWarnings({});
  }, [itemDetail]);

  useEffect(() => {
    fetchItems();
    fetchClientsAndProjects();
  }, [getAuthHeaders]);

  useEffect(() => {
    if (catalogItemId) {
      fetchItemDetail(catalogItemId);
    } else {
      setItemDetail(null);
    }
    setOpenLogFor(null);
  }, [catalogItemId]);

  useEffect(() => {
    if (itemDetail?.item) {
      setEditingItem({ ...itemDetail.item });
    }
  }, [itemDetail]);

  useEffect(() => {
    if (!itemDetail?.item?.id) {
      setDraftComponents([]);
      setNewVersion({ label: '', description: '' });
      setComponentForm({
        child_item_id: '',
        child_version_id: '',
        quantity: 1,
        notes: '',
      });
      setComponentVersions([]);
      setComponentVersionMap({});
      setComponentVersionLoading({});
      return;
    }
    setDraftComponents(buildDraftComponentsFromVersion(itemDetail.versions[0]));
    setNewVersion({ label: '', description: '' });
    setComponentForm({
      child_item_id: '',
      child_version_id: '',
      quantity: 1,
      notes: '',
    });
    setComponentVersions([]);
    setComponentVersionMap({});
    setComponentVersionLoading({});
  }, [itemDetail?.item?.id, itemDetail?.versions?.length, itemDetail?.versions?.[0]?.id]);

  useEffect(() => {
    if (draftComponents.length === 0) return;
    const uniqueIds = Array.from(
      new Set(draftComponents.map((component) => component.child_item_id).filter(Boolean)),
    );
    uniqueIds.forEach((itemId) => {
      const key = String(itemId);
      if (componentVersionMap[key] || componentVersionLoading[key]) return;
      fetchComponentVersions(itemId);
    });
  }, [draftComponents, componentVersionMap, componentVersionLoading]);

  useEffect(() => {
    try {
      localStorage.setItem('inventory_stock_client_filter', stockClientFilter || '');
    } catch (err) {
      // ignore storage errors
    }
  }, [stockClientFilter]);

  useEffect(() => {
    if (stockFormItemId) {
      fetchStockFormVersions(stockFormItemId);
    } else {
      setStockFormVersions([]);
      setNewStock((prev) => ({ ...prev, item_version_id: '' }));
    }
  }, [stockFormItemId]);

  useEffect(() => {
    let active = true;
    const versionId = Number(newStock.item_version_id);
    if (!versionId || !newStock.create_children) {
      setCompatibleLinkableItemIds([]);
      return () => {
        active = false;
      };
    }
    (async () => {
      const ids = await collectCompatibleItemIds(versionId);
      if (active) {
        setCompatibleLinkableItemIds(Array.from(ids));
      }
    })();
    return () => {
      active = false;
    };
  }, [newStock.item_version_id, newStock.create_children]);

  useEffect(() => {
    if (showExistingStockModal) {
      fetchLinkableStockItems();
    } else {
      setSelectedChildStockIds([]);
      setLinkableSearch('');
    }
  }, [showExistingStockModal]);

  useEffect(() => {
    if (showNewItemModal) return;
    setNewItem({ name: '', description: '', purchase_url: '' });
    setNewItemVersion({ label: '', description: '' });
    setNewItemComponents([]);
    setNewItemComponentVersionMap({});
    setNewItemComponentVersionLoading({});
  }, [showNewItemModal]);

  useEffect(() => {
    if (selectedChildStockIds.length > 0 && Number(newStock.quantity) > 1) {
      setNewStock((prev) => ({ ...prev, quantity: 1 }));
    }
  }, [selectedChildStockIds, newStock.quantity]);

  useEffect(() => {
    if (!showQrModal || !qrStockItem) return;
    setQrLoading(true);
    const url = getStockShareUrl(qrStockItem);
    QRCode.toDataURL(url, {
      width: 280,
      margin: 2,
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((dataUrl) => setQrDataUrl(dataUrl))
      .catch(() => flashError('Impossible de generer le QR code'))
      .finally(() => setQrLoading(false));
  }, [showQrModal, qrStockItem]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stockParam = params.get('stock');
    if (!stockParam) return;
    const stockId = Number(stockParam);
    if (!Number.isFinite(stockId)) return;
    const loadDeepLink = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/inventory/stock/${stockId}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error("Occurence non trouvee");
        }
        const data = await response.json();
        setActiveTab('stock');
        setStockStatusFilter('');
        setStockClientFilter('');
        setStockFilterItemId('');
        const serial = data.serial_number || '';
        setStockSearch(serial);
        setPendingExpandStockKey(buildStockGroupKey(data));
      } catch (err) {
        flashError(err.message || "Impossible d'ouvrir l'occurrence");
      }
    };
    loadDeepLink();
  }, [location.search, getAuthHeaders]);

  useEffect(() => {
    if (!pendingExpandStockKey) return;
    const found = groupedStockItems.find(
      (group) => group.group_key === pendingExpandStockKey,
    );
    if (found) {
      setExpandedStockKey(pendingExpandStockKey);
      if (found.group_count === 1 && found.id) {
        fetchStockChildren(found.id);
      }
      setPendingExpandStockKey(null);
    }
  }, [pendingExpandStockKey, groupedStockItems]);

  useEffect(() => {
    if (activeTab === 'stock') {
      fetchStock(currentStockFilters());
    }
  }, [activeTab, stockFilterItemId, stockClientFilter, stockStatusFilter, stockSearch]);

  useEffect(() => {
    setSelectedStockId(null);
  }, [stockFilterItemId, stockClientFilter, stockStatusFilter, stockSearch]);

  return (
    <div className="w-full space-y-8 px-4 pb-10 pt-6">
      <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-white via-white to-slate-50 p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">
                Pilotage des stocks proprietaires
              </h1>
              <p className="text-sm text-slate-500">
                Referentiel produits et suivi des occurrences physiques.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
          >
            <RefreshCw className="h-4 w-4" />
            Rafraichir
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="rounded-full bg-slate-100 px-3 py-1">
            {items.length} items
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1">
            {stockItems.length} occurrences
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-full bg-slate-100 p-1 text-sm shadow-inner">
          <button
            type="button"
            onClick={() => setActiveTab('catalog')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              activeTab === 'catalog'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            PLM
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('stock')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              activeTab === 'stock'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Stock
          </button>
        </div>
      </div>

      {activeTab === 'catalog' && (
        <InventoryCatalogPanel
          items={items}
          projects={projects}
          itemSearch={itemSearch}
          setItemSearch={setItemSearch}
          filteredItems={filteredItems}
          loading={loading}
          catalogItemId={catalogItemId}
          setCatalogItemId={setCatalogItemId}
          setShowNewItemModal={setShowNewItemModal}
          itemDetail={itemDetail}
          editingItem={editingItem}
          setEditingItem={setEditingItem}
          handleDeleteItem={handleDeleteItem}
          handleUpdateItem={handleUpdateItem}
          setActiveTab={setActiveTab}
          currentVersion={currentVersion}
          hasDraftChanges={hasDraftChanges}
          resetDraftChanges={resetDraftChanges}
          draftComponents={draftComponents}
          componentVersionMap={componentVersionMap}
          componentVersionLoading={componentVersionLoading}
          handleDeleteComponent={handleDeleteComponent}
          updateDraftComponent={updateDraftComponent}
          componentForm={componentForm}
          handleComponentItemChange={handleComponentItemChange}
          setComponentForm={setComponentForm}
          componentLoading={componentLoading}
          componentVersions={componentVersions}
          handleAddComponent={handleAddComponent}
          newVersion={newVersion}
          setNewVersion={setNewVersion}
          handleCreateVersion={handleCreateVersion}
          renderCatalogComponents={renderCatalogComponents}
          catalogVersionComponents={catalogVersionComponents}
        />
      )}

      {activeTab === 'stock' && (
        <InventoryStockPanel
          selectedStockId={selectedStockId}
          setSelectedStockId={setSelectedStockId}
          setShowQuickStockModal={setShowQuickStockModal}
          setShowExistingStockModal={setShowExistingStockModal}
          stockItems={stockItems}
          loading={loading}
          fetchStock={fetchStock}
          currentStockFilters={currentStockFilters}
          stockSearch={stockSearch}
          setStockSearch={setStockSearch}
          stockStatusFilter={stockStatusFilter}
          setStockStatusFilter={setStockStatusFilter}
          stockClientFilter={stockClientFilter}
          setStockClientFilter={setStockClientFilter}
          stockFilterItemId={stockFilterItemId}
          setStockFilterItemId={setStockFilterItemId}
          statusOptions={statusOptions}
          clients={clients}
          items={items}
          projects={projectPickerOptions}
          groupedStockItems={groupedStockItems}
          expandedStockKey={expandedStockKey}
          handleToggleExpand={handleToggleExpand}
          statusBorderMap={statusBorderMap}
          statusClassMap={statusClassMap}
          getStatusLabel={getStatusLabel}
          getStockShareUrl={getStockShareUrl}
          openQrModal={openQrModal}
          handleDeleteStock={handleDeleteStock}
          openLogFor={openLogFor}
          setOpenLogFor={setOpenLogFor}
          handleLoadLogs={handleLoadLogs}
          fetchStockChildren={fetchStockChildren}
          renderStockChildrenList={renderStockChildrenList}
          stockChildren={stockChildren}
          loadingChildren={loadingChildren}
          expandedStockNodes={expandedStockNodes}
          handleToggleStockNode={handleToggleStockNode}
          stockLogs={stockLogs}
          newLogMessage={newLogMessage}
          setNewLogMessage={setNewLogMessage}
          handleAddLog={handleAddLog}
          handleUpdateLog={handleUpdateLog}
          handleDeleteLog={handleDeleteLog}
          handleInlineStockUpdate={handleInlineStockUpdate}
          linkableStockItems={linkableStockItems}
          loadingLinkable={loadingLinkable}
          fetchLinkableStockItems={fetchLinkableStockItems}
          handleLinkStockChild={handleLinkStockChild}
          handleUnlinkStockChild={handleUnlinkStockChild}
          handleCreateLinkedChild={handleCreateLinkedChild}
        />
      )}

      <InventoryModals
        showNewItemModal={showNewItemModal}
        setShowNewItemModal={setShowNewItemModal}
        handleCreateItem={handleCreateItem}
        newItem={newItem}
        setNewItem={setNewItem}
        newItemVersion={newItemVersion}
        setNewItemVersion={setNewItemVersion}
        newItemComponentCount={newItemComponentCount}
        items={items}
        handleNewItemComponentItemChange={handleNewItemComponentItemChange}
        handleAddNewItemComponent={handleAddNewItemComponent}
        handleUpdateNewItemComponent={handleUpdateNewItemComponent}
        handleRemoveNewItemComponent={handleRemoveNewItemComponent}
        newItemComponents={newItemComponents}
        newItemComponentVersionMap={newItemComponentVersionMap}
        newItemComponentVersionLoading={newItemComponentVersionLoading}
        showQuickStockModal={showQuickStockModal}
        setShowQuickStockModal={setShowQuickStockModal}
        handleQuickStockCreate={handleQuickStockCreate}
        quickStock={quickStock}
        setQuickStock={setQuickStock}
        loading={loading}
        clients={clients}
        projects={projectPickerOptions}
        showExistingStockModal={showExistingStockModal}
        setShowExistingStockModal={setShowExistingStockModal}
        handleCreateStock={handleCreateStock}
        stockFormItemId={stockFormItemId}
        setStockFormItemId={setStockFormItemId}
        stockFormVersions={stockFormVersions}
        newStock={newStock}
        setNewStock={setNewStock}
        statusOptions={statusOptions}
        selectedChildStockIds={selectedChildStockIds}
        setSelectedChildStockIds={setSelectedChildStockIds}
        filteredLinkableStockItems={filteredLinkableStockItems}
        loadingLinkable={loadingLinkable}
        linkableSearch={linkableSearch}
        setLinkableSearch={setLinkableSearch}
        getStatusLabel={getStatusLabel}
        showQrModal={showQrModal}
        qrStockItem={qrStockItem}
        closeQrModal={closeQrModal}
        qrLoading={qrLoading}
        qrDataUrl={qrDataUrl}
        getStockShareUrl={getStockShareUrl}
        showStockEditModal={showStockEditModal}
        editingStockItem={editingStockItem}
        closeStockEditModal={closeStockEditModal}
        handleUpdateStock={handleUpdateStock}
        stockEdits={stockEdits}
        setStockEdits={setStockEdits}
        handleDeleteStock={handleDeleteStock}
      />

      {(error || success) && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs shadow-lg ${
              error
                ? 'border-rose-200 bg-rose-50 text-rose-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {error ? (
              <XCircle className="h-4 w-4" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            <span>{error || success}</span>
          </div>
        </div>
      )}

    </div>
  );
};

export default InventoryManagement;
